import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import moment from 'moment';
import { IS54FormData } from '../views/pages/NewHire/IS54Form';

const drawTextOnPage = (
  page: any,
  text: string | undefined | null,
  x: number,
  y: number,
  font: any,
  size: number,
  color: any,
) => {
  if (text === undefined || text === null || text === '') return;
  page.drawText(String(text), { x, y, font, size, color });
};

const drawMarkOnPage = (page: any, x: number, y: number, font: any, size: number, color: any, mark = 'X') => {
  page.drawText(mark, {
    x: x + 2.5,
    y: y + 1.5,
    font,
    size,
    color,
  });
};

const bufferToBase64 = (bufferData: { type: string; data: number[] }) => {
  const uint8Array = new Uint8Array(bufferData.data);
  return new TextDecoder().decode(uint8Array);
};

export async function fillIS54Form(fileUrl: string, formData: IS54FormData): Promise<Uint8Array | null> {
  try {
    const arrayBuffer = await fetch(fileUrl).then((res) => res.arrayBuffer());
    const pdfDoc: PDFDocument = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 9;
    const checkboxMarkSize = 10;
    const textColor = rgb(0, 0, 0);

    // Section 1
    drawTextOnPage(firstPage, formData.employerName, 42, 466, font, fontSize, textColor);
    drawTextOnPage(firstPage, formData.dbaName, 42, 413, font, fontSize, textColor);
    drawTextOnPage(firstPage, formData.fein, 42, 360, font, fontSize, textColor);
    drawTextOnPage(firstPage, formData.physicalAddress, 42, 325, font, fontSize, textColor);
    drawTextOnPage(firstPage, formData.mailingAddress, 42, 275, font, fontSize, textColor);
    drawTextOnPage(firstPage, formData.employerPhone, 42, 214, font, fontSize, textColor);

    // Section 2
    if (formData.noticeGivenAtHiring) drawMarkOnPage(firstPage, 50, 165, font, checkboxMarkSize, textColor);
    if (formData.noticeGivenBeforeChange) drawMarkOnPage(firstPage, 50, 149, font, checkboxMarkSize, textColor);

    // Section 3
    drawTextOnPage(firstPage, formData.employeeRateOfPay, 330, 487, font, fontSize, textColor);

    // Section 4
    if (formData.allowanceNone) drawMarkOnPage(firstPage, 300, 444, font, checkboxMarkSize, textColor);
    if (formData.allowanceTips) {
      drawMarkOnPage(firstPage, 300, 428, font, checkboxMarkSize, textColor);
      drawTextOnPage(
        firstPage,
        String(formData.allowanceTipsAmount).includes('$')
          ? formData.allowanceTipsAmount
          : `$${formData.allowanceTipsAmount}`,
        350,
        431,
        font,
        fontSize,
        textColor,
      );
    }
    if (formData.allowanceMeals) {
      drawMarkOnPage(firstPage, 300, 414, font, checkboxMarkSize, textColor);
      drawTextOnPage(
        firstPage,
        String(formData.allowanceMealsAmount).includes('$')
          ? formData.allowanceMealsAmount
          : `$${formData.allowanceMealsAmount}`,
        350,
        416,
        font,
        fontSize,
        textColor,
      );
    }
    if (formData.allowanceLodging) {
      drawMarkOnPage(firstPage, 300, 398, font, checkboxMarkSize, textColor);
      drawTextOnPage(
        firstPage,
        String(formData.allowanceLodgingAmount).includes('$')
          ? formData.allowanceLodgingAmount
          : `$${formData.allowanceLodgingAmount}`,
        368,
        400,
        font,
        fontSize,
        textColor,
      );
    }
    if (formData.allowanceOther) {
      drawMarkOnPage(firstPage, 300, 382, font, checkboxMarkSize, textColor);
      drawTextOnPage(firstPage, formData.allowanceOtherDescription, 345, 384, font, fontSize, textColor);
    }

    // Section 5
    drawTextOnPage(firstPage, formData.regularPayday, 390, 360, font, fontSize, textColor);

    // Section 6
    if (formData.payFrequencySelection === 'weekly')
      drawMarkOnPage(firstPage, 300, 316, font, checkboxMarkSize, textColor);
    if (formData.payFrequencySelection === 'biWeekly')
      drawMarkOnPage(firstPage, 300, 300, font, checkboxMarkSize, textColor);
    if (formData.payFrequencySelection === 'other')
      drawMarkOnPage(firstPage, 300, 284, font, checkboxMarkSize, textColor);

    // Section 7
    drawTextOnPage(firstPage, formData.overtimePayRate, 310, 245, font, fontSize, textColor);

    // Section 8
    if (formData.ackLanguageSelection === 'english') {
      drawMarkOnPage(firstPage, 540, 416, font, checkboxMarkSize, textColor);
    } else if (formData.ackLanguageSelection === 'otherLanguage') {
      drawMarkOnPage(firstPage, 540, 382, font, checkboxMarkSize, textColor);
      drawTextOnPage(firstPage, formData.ackPrimaryLanguage, 680, 384, font, fontSize, textColor);
    }

    // Employee details
    drawTextOnPage(firstPage, formData.printEmployeeName, 540, 310, font, fontSize, textColor);

    // Signature
    let employeeSignature: any = formData.employeeSignature || '';
    if (employeeSignature?.type === 'Buffer') {
      employeeSignature = bufferToBase64(employeeSignature);
    }
    if (employeeSignature?.startsWith('data:image/png;base64,')) {
      try {
        const pngImage = await pdfDoc.embedPng(employeeSignature);
        const sigBoxX = 480;
        const sigBoxY = 260;
        const sigBoxHeight = 35;
        const aspectRatio = pngImage.width / pngImage.height;
        let drawWidth = sigBoxHeight * aspectRatio;
        let drawHeight = sigBoxHeight;
        const maxWidth = 180;

        if (drawWidth > maxWidth) {
          drawWidth = maxWidth;
          drawHeight = maxWidth / aspectRatio;
        }
        const actualSigX = sigBoxX + (maxWidth - drawWidth) / 2;

        firstPage.drawImage(pngImage, {
          x: actualSigX,
          y: sigBoxY + (sigBoxHeight - drawHeight) / 2,
          width: drawWidth,
          height: drawHeight,
        });
      } catch (sigError) {
        console.error('Error embedding signature image:', sigError);
        drawTextOnPage(
          firstPage,
          `(Signature area for ${formData.printEmployeeName || 'employee'})`,
          540,
          272,
          font,
          fontSize - 1,
          textColor,
        );
      }
    } else if (formData.employeeSignature) {
      drawTextOnPage(firstPage, formData.employeeSignature, 540, 272, font, fontSize, textColor);
    }

    const formattedDate = formData.date ? moment(formData.date).format('MM/DD/YYYY') : '';
    drawTextOnPage(firstPage, formattedDate, 540, 236, font, fontSize, textColor);

    drawTextOnPage(firstPage, formData.preparerNameAndTitle, 540, 200, font, fontSize, textColor);

    const modifiedPdfBytes: Uint8Array = await pdfDoc.save();
    return modifiedPdfBytes;
  } catch (error) {
    console.error('Error filling the IS54 PDF:', error);
    return null;
  }
}
